import { ContactsState } from '../../types/contacts/contacts.types';
import {
  GET_CONTACTS_REQUEST,
  GET_CONTACTS_SUCCESS,
  GET_CONTACTS_FAILURE,
  GET_CONTACT_REQUEST,
  GET_CONTACT_SUCCESS,
  GET_CONTACT_FAILURE,
  CREATE_CONTACT_REQUEST,
  CREATE_CONTACT_SUCCESS,
  CREATE_CONTACT_FAILURE,
  UPDATE_CONTACT_REQUEST,
  UPDATE_CONTACT_SUCCESS,
  UPDATE_CONTACT_FAILURE,
  DELETE_CONTACT_REQUEST,
  DELETE_CONTACT_SUCCESS,
  DELETE_CONTACT_FAILURE,
  ContactsActionTypes,
  CLEAR_CONTACT_STATE,
  BULK_CREATE_CONTACTS_REQUEST,
  BULK_CREATE_CONTACTS_SUCCESS,
  BULK_CREATE_CONTACTS_FAILURE,
  BULK_DELETE_CONTACTS_REQUEST,
  BULK_DELETE_CONTACTS_SUCCESS,
  BULK_DELETE_CONTACTS_FAILURE,
  CLEAR_BULK_DELETE_CONTACTS_STATE,
  EXPORT_CONTACTS_CSV_REQUEST,
  EXPORT_CONTACTS_CSV_SUCCESS,
  EXPORT_CONTACTS_CSV_FAILURE,
  CLEAR_EXPORT_STATE,
  DELETE_ALL_CONTACTS_REQUEST,
  DELETE_ALL_CONTACTS_SUCCESS,
  DELETE_ALL_CONTACTS_FAILURE,
  CLEAR_DELETE_ALL_CONTACTS_STATE,
  GET_CONTACT_SEGMENTS_REQUEST,
  GET_CONTACT_SEGMENTS_SUCCESS,
  GET_CONTACT_SEGMENTS_FAILURE,
  GET_CONTACT_ACTIVITY_REQUEST,
  GET_CONTACT_ACTIVITY_SUCCESS,
  GET_CONTACT_ACTIVITY_FAILURE,
  GET_CONTACT_EMAILS_REQUEST,
  GET_CONTACT_EMAILS_SUCCESS,
  GET_CONTACT_EMAILS_FAILURE,
} from './contacts.types';

const initialState: ContactsState = {
  contacts: [],
  selectedContact: null,
  contactFields: [],
  loading: false,
  error: null,
  success: false,
  deleteAllContactsSuccess: false,
  bulkDeleteContactsSuccess: false,
  exportBlob: null,
  pagination: {
    page: 0,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  // Contact segments state
  contactSegments: [],
  contactSegmentsLoading: false,
  contactSegmentsError: null,
  contactSegmentsPagination: {
    page: 0,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  // Contact activity state
  contactActivity: {
    activities: [],
    loading: false,
    error: null,
  },
  // Contact emails state
  contactEmails: [],
  contactEmailsLoading: false,
  contactEmailsError: null,
  contactEmailsPagination: {
    page: 0,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  bulkImportContacts: {
    addedCount: 0,
    updatedCount: 0,
    errorsCount: 0,
    errors: [],
  },
};

const contactsReducer = (
  state: ContactsState = initialState,
  action: ContactsActionTypes
): ContactsState => {
  switch (action.type) {
    case GET_CONTACTS_REQUEST:
    case GET_CONTACT_REQUEST:
    case CREATE_CONTACT_REQUEST:
    case UPDATE_CONTACT_REQUEST:
    case DELETE_CONTACT_REQUEST:
    case BULK_CREATE_CONTACTS_REQUEST:
    case BULK_DELETE_CONTACTS_REQUEST:
    case EXPORT_CONTACTS_CSV_REQUEST:
    case DELETE_ALL_CONTACTS_REQUEST:
      return {
        ...state,
        loading: true,
        selectedContact: null,
        error: null,
      };

    case GET_CONTACT_SEGMENTS_REQUEST:
      return {
        ...state,
        contactSegmentsLoading: true,
        contactSegmentsError: null,
      };

    case GET_CONTACT_ACTIVITY_REQUEST:
      return {
        ...state,
        contactActivity: {
          ...state.contactActivity,
          loading: true,
          error: null,
        },
      };

    case GET_CONTACT_EMAILS_REQUEST:
      return {
        ...state,
        contactEmailsLoading: true,
        contactEmailsError: null,
      };
    case GET_CONTACTS_SUCCESS:
      return {
        ...state,
        loading: false,
        contacts: action.payload.contacts,
        pagination: action.payload.pagination,
        error: null,
      };

    case GET_CONTACT_SUCCESS:
      return {
        ...state,
        loading: false,
        selectedContact: action.payload,
        error: null,
      };

    case CREATE_CONTACT_SUCCESS:
    case UPDATE_CONTACT_SUCCESS:
      const existingIndex = state.contacts.findIndex(
        (contact) =>
          contact._id === action.payload._id ||
          contact.custom_id === action.payload.custom_id
      );

      if (existingIndex !== -1) {
        // Update existing contact
        return {
          ...state,
          loading: false,
          contacts: state.contacts.map((contact) =>
            contact._id === action.payload._id ||
            contact.custom_id === action.payload.custom_id
              ? action.payload
              : contact
          ),
          selectedContact: action.payload,
          error: null,
          success: true,
        };
      } else {
        // Add new contact
        return {
          ...state,
          loading: false,
          contacts: [action.payload, ...state.contacts],
          selectedContact: action.payload,
          error: null,
          success: true,
        };
      }

    case DELETE_CONTACT_SUCCESS:
      return {
        ...state,
        loading: false,
        contacts: state.contacts.filter(
          (contact) =>
            contact._id !== action.payload &&
            contact.custom_id !== action.payload
        ),
        selectedContact:
          state.selectedContact?._id === action.payload ||
          state.selectedContact?.custom_id === action.payload
            ? null
            : state.selectedContact,
        error: null,
      };

    case BULK_CREATE_CONTACTS_SUCCESS:
      return {
        ...state,
        loading: false,
        contacts: [...state.contacts],
        bulkImportContacts: {
          ...state.bulkImportContacts,
          addedCount: action.payload.addedCount,
          updatedCount: action.payload.updatedCount,
          errorsCount: action.payload.errorsCount,
          errors: action.payload.errors,
        },
        error: null,
        success: true,
      };

    case BULK_DELETE_CONTACTS_SUCCESS:
      return {
        ...state,
        loading: false,
        contacts: state.contacts.filter(
          (contact) => !action.payload.includes(contact._id || '')
        ),
        selectedContact:
          state.selectedContact &&
          action.payload.includes(state.selectedContact._id || '')
            ? null
            : state.selectedContact,
        error: null,
        bulkDeleteContactsSuccess: true,
      };

    case EXPORT_CONTACTS_CSV_SUCCESS:
      return {
        ...state,
        loading: false,
        exportBlob: action.payload,
        error: null,
      };

    case DELETE_ALL_CONTACTS_SUCCESS:
      return {
        ...state,
        loading: false,
        contacts: [],
        selectedContact: null,
        pagination: {
          ...state.pagination,
          total: 0,
          page: 0,
        },
        error: null,
        deleteAllContactsSuccess: true,
      };

    case GET_CONTACT_SEGMENTS_SUCCESS:
      return {
        ...state,
        contactSegmentsLoading: false,
        contactSegments: action.payload.segments,
        contactSegmentsPagination: action.payload.pagination,
        contactSegmentsError: null,
      };

    case GET_CONTACT_ACTIVITY_SUCCESS:
      return {
        ...state,
        contactActivity: {
          ...state.contactActivity,
          loading: false,
          activities: action.payload.activities,
          error: null,
        },
      };

    case GET_CONTACT_EMAILS_SUCCESS:
      return {
        ...state,
        contactEmailsLoading: false,
        contactEmails: action.payload.mails,
        contactEmailsPagination: action.payload.pagination,
        contactEmailsError: null,
      };

    case GET_CONTACTS_FAILURE:
    case GET_CONTACT_FAILURE:
    case CREATE_CONTACT_FAILURE:
    case UPDATE_CONTACT_FAILURE:
    case DELETE_CONTACT_FAILURE:
    case BULK_CREATE_CONTACTS_FAILURE:
    case BULK_DELETE_CONTACTS_FAILURE:
    case EXPORT_CONTACTS_CSV_FAILURE:
    case DELETE_ALL_CONTACTS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        selectedContact: null,
        success: false,
      };

    case GET_CONTACT_SEGMENTS_FAILURE:
      return {
        ...state,
        contactSegmentsLoading: false,
        contactSegmentsError: action.payload,
      };

    case GET_CONTACT_ACTIVITY_FAILURE:
      return {
        ...state,
        contactActivity: {
          ...state.contactActivity,
          loading: false,
          error: action.payload,
        },
      };

    case GET_CONTACT_EMAILS_FAILURE:
      return {
        ...state,
        contactEmailsLoading: false,
        contactEmailsError: action.payload,
      };
    case CLEAR_CONTACT_STATE:
      return {
        ...state,
        success: false,
        error: null,
      };

    case CLEAR_EXPORT_STATE:
      return {
        ...state,
        exportBlob: null,
        error: null,
      };

    case CLEAR_DELETE_ALL_CONTACTS_STATE:
      return {
        ...state,
        deleteAllContactsSuccess: false,
        error: null,
      };

    case CLEAR_BULK_DELETE_CONTACTS_STATE:
      return {
        ...state,
        bulkDeleteContactsSuccess: false,
        error: null,
      };
    default:
      return state;
  }
};

export default contactsReducer;
